import {
    Alert,
    Box,
    Button,
    Card,
    CardActions,
    CardContent,
    Checkbox,
    Chip,
    Divider,
    FormControl,
    FormControlLabel,
    FormLabel,
    Radio,
    RadioGroup,
    Stack,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Typography,
} from '@mui/material';
import { ChangeEvent, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useHistory } from 'react-router-dom';
import { useAccessInfo } from '../../contexts/AccessTokenProvider';
import { useErrorDisplay } from '../../components/ErrorDisplay';
import BusySpinner from '../../components/BusySpinner';
import PanelSegment from '../../components/PanelSegment';
import PageTitle from '../../components/PageTitle';
import useViewportMode from '../../hooks/useViewportMode';
import { Securables } from '../../Enums';
import ProducerPortalClient from '../../apiClients/ProducerPortalClient';
import {
    ProducerPayoutRun,
    ProducerPayoutRunCandidate,
    ProducerPayoutRunPreview,
    ProducerPayoutRunStartPayload,
} from '../../models/producer';

const money = (value?: number) =>
    new Intl.NumberFormat(undefined, { style: 'currency', currency: 'USD', minimumFractionDigits: 2, maximumFractionDigits: 2 }).format((value || 0) / 100);

const runStatusTone = (state?: ProducerPayoutRun['Status']): 'default' | 'info' | 'warning' | 'success' | 'error' => {
    if (state === 'Completed' || state === 'Reconciling')
        return 'success';
    if (state === 'Failed' || state === 'PartiallyFailed')
        return 'error';
    if (state === 'Cancelled')
        return 'warning';
    return 'info';
};

const isRunningStatus = (state?: ProducerPayoutRun['Status']) =>
    state === 'Queued' || state === 'Running';

const rowsFromCandidates = (candidates: ProducerPayoutRunCandidate[]) => candidates.map(candidate => ({
    ...candidate,
    key: candidate.ProducerCompanyId,
}));

const runRows = (runs: ProducerPayoutRun[], onRetry: (runId: string) => void) => runs.map(run => (
    <TableRow key={run.Id}>
        <TableCell>{run.ProducerCompanyName}</TableCell>
        <TableCell align="right">{run.TotalAmountCents ? money(run.TotalAmountCents) : '-'}</TableCell>
        <TableCell><Chip size="small" color={runStatusTone(run.Status)} label={run.Status} /></TableCell>
        <TableCell>{run.ReconciliationStatus || '-'}</TableCell>
        <TableCell>{run.FailureMessage || '-'}</TableCell>
        <TableCell>{run.StartedDate || '-'}</TableCell>
        <TableCell>{run.CompletedDate || '-'}</TableCell>
        <TableCell>
            {run.CanRetry ? <Button size="small" color="warning" onClick={() => onRetry(run.Id)} disabled={isRunningStatus(run.Status)}>
                Retry payout
            </Button> : '-'}
        </TableCell>
    </TableRow>
));

const createIdempotencyKey = (): string => {
    if (typeof window !== 'undefined' && window.crypto?.randomUUID)
        return window.crypto.randomUUID();

    return `run-${Date.now().toString(36)}-${Math.floor(Math.random() * 1_000_000).toString(36)}`;
};

const ProducerPayoutRunsPage = () => {
    const history = useHistory();
    const { mode, isDesktop } = useViewportMode();
    const { hasRole } = useAccessInfo();
    const [ErrorDisplay, setError] = useErrorDisplay();
    const [busy, setBusy] = useState(false);
    const [running, setRunning] = useState(false);
    const [candidates, setCandidates] = useState<ProducerPayoutRunCandidate[]>([]);
    const [historyRows, setHistoryRows] = useState<ProducerPayoutRun[]>([]);
    const [selectedProducerId, setSelectedProducerId] = useState<string>(null);
    const [selectionMode, setSelectionMode] = useState<'single' | 'all'>('single');
    const [preview, setPreview] = useState<ProducerPayoutRunPreview>(null);
    const [activeRuns, setActiveRuns] = useState<ProducerPayoutRun[]>([]);
    const [needConfirm, setNeedConfirm] = useState(false);
    const idempotencyKeys = useRef<Record<string, string>>({});
    const canManage = hasRole(Securables.synergist_admin, true);

    const eligibleProducerIds = useMemo(() => candidates.filter(candidate => candidate.IsEligible).map(candidate => candidate.ProducerCompanyId), [candidates]);

    const selectedCandidate = useMemo(() =>
        candidates.find(candidate => candidate.ProducerCompanyId === selectedProducerId),
    [candidates, selectedProducerId]);

    const load = useCallback(async () => {
        if (!canManage)
            return;

        try {
            setBusy(true);
            const [candidatePayload, historyPayload] = await Promise.all([
                new ProducerPortalClient().GetPayoutRunEligibleProducers(),
                new ProducerPortalClient().GetPayoutRunHistory(),
            ]);
            setCandidates(candidatePayload);
            setHistoryRows(historyPayload);
        } catch (error) {
            setError(error, 'Could not load payout-run candidates and history.');
        } finally {
            setBusy(false);
        }
    }, [canManage, setError]);

    const loadActiveRuns = useCallback(async () => {
        if (!activeRuns.length)
            return;

        try {
            const nextRuns = await Promise.all(activeRuns.map(item =>
                new ProducerPortalClient().GetPayoutRun(item.Id),
            ));
            setActiveRuns(nextRuns);
            const stillRunning = nextRuns.some(item => isRunningStatus(item.Status));
            setRunning(stillRunning);
            if (!stillRunning)
                await load();
        } catch (error) {
            setError(error, 'Failed to refresh active payout run status.');
            setRunning(false);
        }
    }, [activeRuns, load, setError]);

    const loadPreview = async (mode: 'single' | 'all', candidateId: string | null) => {
        if (!canManage)
            return;

        if (mode === 'single' && !candidateId)
            return;

        if (mode === 'all' && eligibleProducerIds.length === 0)
            return;

        try {
            setBusy(true);
            const response = mode === 'single'
                ? await new ProducerPortalClient().GetPayoutRunPreview(candidateId)
                : await new ProducerPortalClient().GetPayoutRunPreview();
            setPreview(response);
            setNeedConfirm(false);
        } catch (error) {
            setError(error, 'Could not load payout run preview.');
        } finally {
            setBusy(false);
        }
    };

    const startRun = async () => {
        if (!preview || !needConfirm || !preview.PreviewFingerprint)
            return;

        if (selectionMode === 'single' && !selectedProducerId)
            return;

        const requestIdentity = `${selectionMode}:${selectedProducerId || 'all'}:${preview.PreviewFingerprint}`;
        const idempotencyKey = idempotencyKeys.current[requestIdentity] || createIdempotencyKey();
        idempotencyKeys.current[requestIdentity] = idempotencyKey;
        const payload: ProducerPayoutRunStartPayload = {
            IdempotencyKey: idempotencyKey,
            PreviewFingerprint: preview.PreviewFingerprint,
        };

        try {
            setRunning(true);
            setBusy(true);
            const response = await new ProducerPortalClient().StartPayoutRun(payload, selectionMode === 'single' ? selectedProducerId : null);
            const runs = [response];
            setActiveRuns(runs);
            setRunning(runs.some(run => isRunningStatus(run.Status)));
            if (!runs.some(run => isRunningStatus(run.Status)))
                await load();
            setNeedConfirm(false);
        } catch (error) {
            setError(error, 'Payout run could not be started.');
            setRunning(false);
        } finally {
            setBusy(false);
        }
    };

    const retryRun = async (runId: string) => {
        if (!runId)
            return;

        try {
            setBusy(true);
            const retryResult = await new ProducerPortalClient().RetryPayoutRun(runId);
            const runs = [retryResult];
            setActiveRuns(runs);
            setRunning(runs.some(run => isRunningStatus(run.Status)));
            if (!runs.some(run => isRunningStatus(run.Status)))
                await load();
            setNeedConfirm(false);
        } catch (error) {
            setError(error, 'Failed to retry payout run.');
        } finally {
            setBusy(false);
        }
    };

    const selectCandidate = async (candidate: ProducerPayoutRunCandidate) => {
        if (!candidate.IsEligible)
            return;

        setSelectionMode('single');
        setSelectedProducerId(candidate.ProducerCompanyId);
        setActiveRuns([]);
        setNeedConfirm(false);
        await loadPreview('single', candidate.ProducerCompanyId);
    };

    const handleModeChange = async (_event: ChangeEvent<HTMLInputElement>, value: string) => {
        const nextMode = value as 'single' | 'all';
        setSelectionMode(nextMode);
        setActiveRuns([]);
        setNeedConfirm(false);
        setSelectedProducerId(null);
        setPreview(null);

        if (nextMode === 'all')
            await loadPreview('all', null);
    };

    useEffect(() => {
        void load();
    }, [canManage, load]);

    useEffect(() => {
        if (!running)
            return;

        const timer = window.setInterval(() => {
            void loadActiveRuns();
        }, 1000);

        return () => {
            window.clearInterval(timer);
        };
    }, [running, loadActiveRuns]);

    if (!canManage)
        return <Alert severity="warning">Only Synergist admins can run Producer payouts.</Alert>;

    return <Box data-viewport-mode={mode}>
        <ErrorDisplay />
        <BusySpinner busy={busy || running}>
            <PanelSegment
                titleContent={<PageTitle title="Producer payout runs" />}
                toolbar={
                    <Stack direction="row" spacing={1} alignItems="center">
                        <Button size="small" variant="outlined" onClick={() => history.push('/SystemAdministration/Producers')}>Producers</Button>
                        <Button size="small" variant="text" onClick={() => void load()}>Refresh</Button>
                    </Stack>
                }
            >
                <Box sx={{ p: 2 }}>
                    <FormControl sx={{ mb: 2 }}>
                        <FormLabel id="payout-run-mode-label">Run mode</FormLabel>
                        <RadioGroup row aria-labelledby="payout-run-mode-label" value={selectionMode} onChange={handleModeChange}>
                            <FormControlLabel value="single" control={<Radio />} label="Single producer" />
                            <FormControlLabel value="all" control={<Radio />} label={`All eligible producers (${eligibleProducerIds.length})`} />
                        </RadioGroup>
                    </FormControl>

                    <Typography variant="subtitle1" sx={{ mb: 1 }}>Step 1: pick payout run scope</Typography>
                    <TableContainer component={Card} sx={{ mb: 2 }}>
                        <Table size="small">
                            <TableHead>
                                <TableRow>
                                    <TableCell>Producer</TableCell>
                                    <TableCell align="right">Estimated payout</TableCell>
                                    <TableCell>Eligibility</TableCell>
                                    <TableCell>Action</TableCell>
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {rowsFromCandidates(candidates).length === 0 && (
                                    <TableRow>
                                        <TableCell colSpan={4} sx={{ color: 'text.secondary' }}>No producers are currently eligible for payout runs.</TableCell>
                                    </TableRow>
                                )}
                                {rowsFromCandidates(candidates).map(candidate => <TableRow key={candidate.key}>
                                    <TableCell>{candidate.ProducerCompanyName}</TableCell>
                                    <TableCell align="right">{money(candidate.EstimatedPayoutCents)}</TableCell>
                                    <TableCell>{candidate.IsEligible ? 'Ready' : candidate.EligibilityReason || 'Not ready'}</TableCell>
                                    <TableCell>
                                        <Button
                                            size="small"
                                            variant={selectionMode === 'single' && selectedProducerId === candidate.ProducerCompanyId ? 'contained' : 'outlined'}
                                            disabled={!candidate.IsEligible || (selectionMode === 'all')}
                                            onClick={() => void selectCandidate(candidate)}
                                        >
                                            {selectionMode === 'single' && selectedProducerId === candidate.ProducerCompanyId ? 'Selected' : 'Preview'}
                                        </Button>
                                    </TableCell>
                                </TableRow>)}
                            </TableBody>
                        </Table>
                    </TableContainer>

                    {selectionMode === 'all' && preview && (
                        <>
                            <Divider sx={{ mb: 2 }} />
                            <Typography variant="subtitle1" sx={{ mb: 1 }}>Step 2: batch preview</Typography>
                            <Card variant="outlined" sx={{ mb: 1.5 }}>
                                <CardContent>
                                    <Typography variant="subtitle2" sx={{ mb: 0.5 }}>All selected eligible producers</Typography>
                                    <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                                        Total estimated payout: {money(preview.TotalAmountCents)}
                                    </Typography>
                                    <TableContainer>
                                        <Table size="small" sx={{ mt: 1.5 }}>
                                            <TableHead>
                                                <TableRow>
                                                    <TableCell>Producer</TableCell>
                                                    <TableCell align="right">Estimated payout</TableCell>
                                                </TableRow>
                                            </TableHead>
                                            <TableBody>
                                                {(preview.Producers || []).filter(producer => producer.IsEligible).length === 0 && (
                                                    <TableRow><TableCell colSpan={2} sx={{ color: 'text.secondary' }}>No producer detail available.</TableCell></TableRow>
                                                )}
                                                {(preview.Producers || []).filter(producer => producer.IsEligible).map(producer => <TableRow key={producer.ProducerCompanyId}>
                                                    <TableCell>{producer.ProducerCompanyName}</TableCell>
                                                    <TableCell align="right">{money(producer.EstimatedPayoutCents)}</TableCell>
                                                </TableRow>)}
                                            </TableBody>
                                        </Table>
                                    </TableContainer>
                                </CardContent>
                            </Card>
                        </>
                    )}

                    {selectionMode === 'single' && selectedCandidate && preview && (
                        <>
                            <Divider sx={{ mb: 2 }} />
                            <Typography variant="subtitle1" sx={{ mb: 1 }}>Step 2: preview and confirm selected producer</Typography>
                            <Card variant="outlined" sx={{ mb: 1.5 }}>
                                <CardContent>
                                    <Typography variant="subtitle2" sx={{ mb: 0.5 }}>Selected producer</Typography>
                                    <Typography>{preview.ProducerCompanyName}</Typography>
                                    <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                                        Total estimated payout: {money(preview.TotalAmountCents)}
                                    </Typography>
                                    <TableContainer>
                                        <Table size="small" sx={{ mt: 1.5 }}>
                                            <TableHead>
                                                <TableRow>
                                                    <TableCell>Item</TableCell>
                                                    <TableCell align="right">Quantity</TableCell>
                                                    <TableCell align="right">Amount</TableCell>
                                                </TableRow>
                                            </TableHead>
                                            <TableBody>
                                                {(preview.LineItems || []).length === 0 && (
                                                    <TableRow><TableCell colSpan={3} sx={{ color: 'text.secondary' }}>No billed activity is currently available.</TableCell></TableRow>
                                                )}
                                                {(preview.LineItems || []).map((line, index) => <TableRow key={`${line.Label}-${index}`}>
                                                    <TableCell>{line.Label}</TableCell>
                                                    <TableCell align="right">{line.Quantity ?? 0}</TableCell>
                                                    <TableCell align="right">{money(line.AmountCents)}</TableCell>
                                                </TableRow>)}
                                            </TableBody>
                                        </Table>
                                    </TableContainer>
                                </CardContent>
                            </Card>
                        </>
                    )}

                    <Stack direction={isDesktop ? 'row' : 'column'} alignItems={isDesktop ? 'center' : 'stretch'} spacing={1} sx={{ mb: 2 }}>
                        <Stack direction="row" spacing={1} alignItems="center">
                            <Checkbox
                                inputProps={{ 'aria-label': 'Confirm payout execution' }}
                                checked={needConfirm}
                                onChange={(event) => setNeedConfirm(event.target.checked)}
                                disabled={!preview || !preview.PreviewFingerprint}
                            />
                            <Typography variant="body2">I confirm all payout line items and totals before execution.</Typography>
                        </Stack>
                        <Button
                            variant="contained"
                            onClick={() => void startRun()}
                            disabled={!selectionMode || !preview || !needConfirm || running}
                        >
                            Execute payout run
                        </Button>
                    </Stack>

                    {activeRuns.length > 0 && (
                        <>
                            <Divider sx={{ mb: 2 }} />
                            <Typography variant="subtitle1" sx={{ mb: 1 }}>Step 3: execution progress</Typography>
                            <Card variant="outlined">
                                <CardContent>
                                    <Stack spacing={1.5}>
                                        {(activeRuns || []).map(run => <Stack key={run.Id} direction="row" justifyContent="space-between" alignItems="center">
                                            <Stack>
                                                <Typography fontWeight={700}>{run.ProducerCompanyName}</Typography>
                                                <Typography color="text.secondary" variant="body2">{run.Id}</Typography>
                                                <Typography variant="body2">Reconciliation: {run.ReconciliationStatus || 'Pending'}</Typography>
                                                <Typography variant="body2">Run result: {run.FailureMessage || 'In progress'}</Typography>
                                                <Typography variant="body2">Estimated amount: {money(run.TotalAmountCents)}</Typography>
                                            </Stack>
                                            <Stack spacing={1} direction="row" flexWrap="wrap">
                                                <Chip size="small" color={runStatusTone(run.Status)} label={run.Status} />
                                                <Chip size="small" label={`Retryable: ${run.CanRetry ? 'Yes' : 'No'}`} color={run.CanRetry ? 'warning' : 'default'} />
                                                {run.CanRetry && <Button
                                                    size="small"
                                                    color="warning"
                                                    onClick={() => void retryRun(run.Id)}
                                                    disabled={isRunningStatus(run.Status)}
                                                >
                                                    Retry payout
                                                </Button>}
                                            </Stack>
                                        </Stack>)}
                                    </Stack>
                                </CardContent>
                                <CardActions sx={{ px: 2, pb: 2 }}>
                                    <Button
                                        size="small"
                                        variant="text"
                                        onClick={() => void loadActiveRuns()}
                                        disabled={!activeRuns.some(run => isRunningStatus(run.Status))}
                                    >
                                        Refresh status
                                    </Button>
                                </CardActions>
                            </Card>
                        </>
                    )}

                    <Divider sx={{ my: 2 }} />
                    <Typography variant="subtitle1" sx={{ mb: 1 }}>Immutable payout run history</Typography>
                    <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                        This view is append-only for reconciliation and audit retention.
                    </Typography>
                    <TableContainer component={Card}>
                        <Table size="small">
                            <TableHead>
                                <TableRow>
                                    <TableCell>Producer</TableCell>
                                    <TableCell align="right">Amount</TableCell>
                                    <TableCell>Status</TableCell>
                                    <TableCell>Reconciliation</TableCell>
                                    <TableCell>Failure</TableCell>
                                    <TableCell>Started</TableCell>
                                    <TableCell>Completed</TableCell>
                                    <TableCell>Actions</TableCell>
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {historyRows.length === 0 && (
                                    <TableRow>
                                        <TableCell colSpan={8} sx={{ color: 'text.secondary' }}>No payout runs have been executed yet.</TableCell>
                                    </TableRow>
                                )}
                                {runRows(historyRows, retryRun)}
                            </TableBody>
                        </Table>
                    </TableContainer>
                </Box>
            </PanelSegment>
        </BusySpinner>
    </Box>;
};

export default ProducerPayoutRunsPage;
